import { Component, DestroyRef, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { NgForm, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { DxDataGridModule } from 'devextreme-angular';
import { InitNewRowEvent } from 'devextreme/ui/data_grid';
import { DxoEditingModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule } from 'devextreme-angular/ui/nested';
import { ScheduleItem, Trigger } from 'src/app/shared/scheduling/models';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { GenericModalService } from 'src/app/shared/generic-modal/generic-modal.service';
import { SvgIconComponent } from 'src/app/shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from 'src/app/shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from 'src/app/shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from 'src/app/shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { BizDataGridDirective } from 'src/app/shared/grid/biz-grid.directive';
import { ScheduleItemComponent } from 'src/app/shared/scheduling/schedule-item.component';
import { ReportConfiguration, ReportEntity, ReportParameterEntity, ScheduledItemEntity, TriggerEntity } from '../../models/report-configuration';
import { SendAsOptions } from '../../models/report-send-as-options';
import { ReportsService } from '../../services/reports.service';
import { UniqueReportNameValidatorDirective } from './reports-edit-report-unique-name.validator';
import { DxValidationGroupComponent, DxValidationGroupModule } from 'devextreme-angular';
import { TimeZoneService } from 'src/app/shared/globalization/services/time-zone-service';

@Component({
  templateUrl: './reports-edit-report.component.html',
  styleUrls: ['./reports-edit-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    UniqueReportNameValidatorDirective,
    SvgIconComponent,
    PermissionRequiredDirective,
    ChangeLogLinkComponent,
    ChangeLogLinkEntityDirective,
    NgTemplateOutlet,
    FormsModule,
    DxDataGridModule,
    BizDataGridDirective,
    DxoEditingModule,
    DxiColumnModule,
    DxiValidationRuleModule,
    DxoLookupModule,
    ScheduleItemComponent,
    DxValidationGroupModule]
})
export class ReportsEditReportComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly modalService = inject(GenericModalService);
  private readonly toastService = inject(ToastService);
  private readonly loadingPanelService = inject(LoadingPanelService);
  private readonly reportsService = inject(ReportsService);
  private readonly timeZoneService = inject(TimeZoneService);

  @ViewChild('myForm', { read: NgForm }) private myForm!: NgForm;
  @ViewChild(DxValidationGroupComponent) validationGroup: DxValidationGroupComponent;
  @ViewChild('reportSchedule') private reportSchedule?: ScheduleItemComponent;
  @ViewChild(BizDataGridDirective) private parameterGrid?: BizDataGridDirective;

  readonly vm = {
    editing: false,
    gridEditing: false,
    report: new ReportEntity(),
    parameters: [] as ReportParameterEntity[],
    scheduledItem: new ScheduleItem(),
  };

  readonly sendAsOptions: ReadonlyArray<{ id: SendAsOptions; name: string; }> = [
    { id: SendAsOptions.CommaSeparated, name: 'CSV (comma separated)' },
    { id: SendAsOptions.SemicolonSeparated, name: 'CSV (semicolon separated)' },
  ];

  readonly parameterTypes: ReadonlyArray<{ id: string; name: string; }> = [
    { id: 'BIT', name: 'Bit' },
    { id: 'INT', name: 'Int' },
    { id: 'DATETIME', name: 'Datetime' },
    { id: 'NVARCHAR(100)', name: 'NVarChar(100)' },
    { id: 'NVARCHAR(500)', name: 'NVarChar(500)' },
    { id: 'NVARCHAR(MAX)', name: 'NVarChar(Max)' },
  ];

  ngOnInit(): void {
    this.route.paramMap.pipe(
      map(paramMap => Number(paramMap.get('id'))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(id => {
      void this.refresh(id).then(() => {
        if (!id) {
          this.edit();
        }
      });
    });
  }

  async refresh(id?: number): Promise<void> {
    id ??= this.vm.report.Id;

    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading report configuration');

      const reportConfiguration = id
        ? (await this.reportsService.getReportConfigurations(id)).shift() ?? this.createDefaultReportConfiguration(id)
        : this.createDefaultReportConfiguration(id);
      this.updateViewModel(reportConfiguration);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  private createDefaultReportConfiguration(id?: number): ReportConfiguration {
    return new ReportConfiguration({
      Report: new ReportEntity({ Id: id }),
      Triggers: [new TriggerEntity({ Id: null, StartTime: '08:00:00', Weekdays: 'Monday, Tuesday, Wednesday, Thursday, Friday', TimeZoneIdentifier: this.timeZoneService.localTimeZone().Identifier })],
    });
  }

  private updateViewModel(reportConfiguration: ReportConfiguration): void {
    this.vm.report = reportConfiguration.Report;
    this.vm.parameters = reportConfiguration.ReportParameters?.map(e => {
      e.Type = e.Type.toLocaleUpperCase();
      return e;
    }) ?? [];
    this.vm.scheduledItem = this.convertToScheduleItem(reportConfiguration.ScheduledItem, reportConfiguration.Triggers);
  }

  private convertToScheduleItem(scheduledItemInfo: ScheduledItemEntity, triggers: TriggerEntity[]): ScheduleItem {
    const newDate = new Date();
    newDate.setHours(0, 0, 0, 0);
    const si = new ScheduleItem();
    si.Id = scheduledItemInfo ? scheduledItemInfo.Id : 0;
    si.Enabled = scheduledItemInfo ? scheduledItemInfo.Enabled : false;
    si.StartDate = scheduledItemInfo ? scheduledItemInfo.StartDate : newDate;
    si.EndDate = scheduledItemInfo ? scheduledItemInfo.EndDate : null;
    si.Triggers = triggers ? triggers.map(e => this.convertToTrigger(e)) : [];
    return si;
  }

  private convertToTrigger(trigger: TriggerEntity): Trigger {
    const t = new Trigger();
    t.Id = trigger?.Id;
    t.StartTime = trigger?.StartTime ?? '';
    t.EndTime = trigger?.EndTime ?? '';
    t.Weekdays = trigger?.Weekdays ?? '';
    t.TimeZoneIdentifier = trigger?.TimeZoneIdentifier ?? null;
    return t;
  }

  private convertToScheduledItemEntity(scheduleItem: ScheduleItem): ScheduledItemEntity {
    const sie = new ScheduledItemEntity({
      Id: scheduleItem.Id,
      StartDate: scheduleItem.StartDate ?? new Date(),
      EndDate: scheduleItem.EndDate,
      Enabled: scheduleItem.Enabled,
    });
    return sie;
  }

  private convertToTriggerEntity(trigger: Trigger): TriggerEntity {
    const t = new TriggerEntity({
      Id: trigger.Id,
      Weekdays: trigger.Weekdays,
      StartTime: trigger.StartTime,
      EndTime: trigger.EndTime,
      TimeZoneIdentifier: trigger.TimeZoneIdentifier
    });
    return t;
  }

  addNewItem(): void {
    this.goToReport(0);
  }

  edit(): void {
    this.vm.editing = true;
  }

  private finishEditing(): void {
    this.vm.editing = false;
    this.parameterGrid?.instance?.clearFilter();
  }

  private goToReport(id: number): void {
    void this.router.navigate(['.', { id }], { relativeTo: this.route });
  }

  private goToRun(id: number): void {
    void this.router.navigate(['../run', { id }], { relativeTo: this.route });
  }

  private goToOverview(): void {
    void this.router.navigate(['..'], { relativeTo: this.route });
  }

  readonly onInitNewRow = (ev: InitNewRowEvent<ReportParameterEntity, number>): void => {
    ev.data = new ReportParameterEntity({ Name: '@i', Type: 'INT' });
  };

  readonly onEditingStart = (): void => {
    this.vm.gridEditing = true;
  };
  readonly onEditingEnd = (): void => {
    this.vm.gridEditing = false;
  };

  canSave(): boolean {
    const validation = this.validationGroup?.instance?.validate();
    const scheduleValid = this.reportSchedule?.isScheduleValid ?? true;

    return this.myForm.valid
      && !this.vm.gridEditing
      && !!validation && validation.isValid
      && (!this.vm.scheduledItem.Enabled || (this.vm.scheduledItem.Triggers?.length ?? 0) > 0)
      && scheduleValid;
  }

  async save(): Promise<void> {
    const reportConfiguration = new ReportConfiguration({
      Report: this.vm.report,
      ReportParameters: this.vm.parameters,
      ScheduledItem: this.convertToScheduledItemEntity(this.vm.scheduledItem),
      Triggers: this.vm.scheduledItem.Triggers.map(e => this.convertToTriggerEntity(e)),
    });

    try {
      this.finishEditing();

      const saved = await this.reportsService.saveReportConfiguration(reportConfiguration);
      this.updateViewModel(saved);

      this.toastService.showToast('Report saved...', ToastType.success);
    }
    catch (err) {
      console.error(err);
      this.toastService.showToast('Failed to save report...', ToastType.error);
    }

    if (reportConfiguration.Report.Id !== this.vm.report.Id) {
      // When save returns a new id, i.e. after add new item
      this.goToReport(this.vm.report.Id);
    }
  }

  cancel(): void {
    this.finishEditing();
    this.reportSchedule?.clearValidationErrors();
    if (this.vm.report.Id) {
      this.goToReport(this.vm.report.Id);
    }
    else {
      // When there is no id, we must be canceling an add new operation
      this.goToOverview();
    }
     void this.refresh();
  }

  async delete(): Promise<void> {
    try {
      await this.modalService.confirmDirect('Delete report', 'Are you sure you want to delete this report?');
      await this.reportsService.deleteReportConfiguration(this.vm.report.Id);
      this.goToOverview();
    }
    catch {
      // Do nothing
    }
  }

  runReport(): void {
    this.goToRun(this.vm.report.Id);
  }
}
